import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stack, Group, Text, Badge, Button, Card, SimpleGrid } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import {
  Alert,
  DataTable,
  EmptyState,
  FilterDrawer,
  PageHeader,
  StatusBadge,
  TableShell,
  type FilterField,
  type FilterValues,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { admin } from '@repletoryreach/sdk-api';
import { usePersistedState } from '../../../lib/usePersistedState';
import { StatCard } from '../../../components/StatCard';
import { CurrencySwitch, amountIn, useDisplayCurrency } from '../../../components/CurrencySwitch';
import { ActiveFilterPills, type ActiveFilterPill } from '../../../components/ActiveFilterPills';
import { TableFooter } from '../../../components/TableFooter';
import { CopyableId } from '../../../components/CopyableId';
import { TwoLineCell } from '../../../components/TwoLineCell';
import { PlanEditDrawer } from './components/PlanEditDrawer';
import { BillingToolbar } from '../components/BillingToolbar';
import { ViewToggle } from '../components/ViewToggle';
import { Money } from '../components/cells';
import { IconClock, IconLayers, IconTrend, IconWallet } from '../components/icons';
import { formatDate, formatInteger, formatMinor } from '../../../lib/format';
import { DEFAULT_MARKUP_PERCENT, entryPlan, formatCredits, includedCeilingMinor, wholeCredits } from '../../../lib/priceLadder';
import type { Indexed } from '../../../lib/types';
import { useT } from '../../../i18n/I18nProvider';
import '../components/billing.css';

const MUTED = 'var(--rr-color-text-muted)';

/**
 * `EntityCardGrid` still types `cols` as a plain number, so the card view uses
 * `SimpleGrid` directly to get the responsive map. Plan cards are not
 * clickable, so nothing else the composite provides is lost.
 */
const GRID_COLS = { base: 1, sm: 2, lg: 3 };
const STAT_COLS = { base: 1, sm: 2, lg: 4 };

type Plan = admin.AdminPlan;

/**
 * `DataTable` sorts on the record's own fields, so the subscriber count and the
 * per-plan revenue are folded into the row rather than computed in `render`.
 * Without that, "sort plans by popularity" cannot work. The markup and the
 * ceiling are folded in for the same reason, and so that a row the service sent
 * without them still reads as a number.
 */
type PlanRow = Plan & { subscribers: number; mrr_minor: number; markup: number; ceiling_minor: number };

export default function PlansPage() {
  const t = useT();
  const nav = useNavigate();
  const plansQ = useQuery({ queryKey: ['admin', 'plans'], queryFn: () => admin.listPlans() });
  // The subscription roster itself lives on `/billing/subscriptions`. This page
  // reads the same cached query only to count subscribers and derive per-plan
  // revenue, which is what a plan catalogue is missing without it.
  const subsQ = useQuery({ queryKey: ['admin', 'subscriptions'], queryFn: () => admin.listSubscriptions() });

  const plansAll = useMemo(() => (plansQ.data?.data ?? []) as Plan[], [plansQ.data]);
  const subsAll = useMemo(() => subsQ.data?.data ?? [], [subsQ.data]);

  const subsByPlan = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of subsAll) if (s.status === 'active') m.set(s.plan_id, (m.get(s.plan_id) ?? 0) + 1);
    return m;
  }, [subsAll]);

  const rows: PlanRow[] = useMemo(
    () => plansAll.map((p) => {
      const subscribers = subsByPlan.get(p.id) ?? 0;
      const markup = p.markup_percent ?? DEFAULT_MARKUP_PERCENT;
      // The service sends the ceiling. The same arithmetic stands in for a row without one.
      const ceiling_minor = p.included_ceiling_minor ?? includedCeilingMinor(p.monthly_amount_minor, markup);
      return { ...p, subscribers, mrr_minor: p.monthly_amount_minor * subscribers, markup, ceiling_minor };
    }),
    [plansAll, subsByPlan]
  );

  const archivedWithSubs = (p: PlanRow) => !p.is_active && p.subscribers > 0;
  // More included credits than the fee can carry at the plan's markup: a
  // customer who spends them all costs more than they paid.
  const overCeiling = (p: PlanRow) => p.included_credits_minor > p.ceiling_minor;
  // The cheapest plan on sale. Its markup is the pay-as-you-go price.
  const entryId = useMemo(() => entryPlan(plansAll)?.id, [plansAll]);

  const [view, setView] = usePersistedState<ViewMode>('admin.plans.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.plans.pageSize', 25);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Plan | null>(null);

  const activeSubs = subsAll.filter((s) => s.status === 'active').length;

  // Money totals are kept per currency: a sum across currencies means nothing,
  // and three joined figures overflowed the card. The cards show one currency,
  // chosen below and remembered across the billing pages.

  const mrrByCurrency = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of rows) if (p.mrr_minor) m.set(p.currency, (m.get(p.currency) ?? 0) + p.mrr_minor);
    return m;
  }, [rows]);

  /**
   * Average revenue per tenant, per currency. The division happens on the
   * minor-unit integer and the result is rounded before `formatMinor` sees it,
   * so no fraction of a cent is ever printed.
   */
  const arptByCurrency = useMemo(() => {
    const m = new Map<string, number>();
    if (activeSubs === 0) return m;
    for (const [currency, minor] of mrrByCurrency) m.set(currency, Math.round(minor / activeSubs));
    return m;
  }, [mrrByCurrency, activeSubs]);

  /**
   * What the platform failed to collect: the plan price behind every past-due
   * subscription. Subscriptions carry no amount of their own, so the figure
   * comes from the plan the subscription is on.
   */
  const pastDueByCurrency = useMemo(() => {
    const priceOf = new Map(plansAll.map((p) => [p.id, p]));
    const m = new Map<string, number>();
    for (const s of subsAll) {
      if (s.status !== 'past_due') continue;
      const plan = priceOf.get(s.plan_id);
      if (!plan) continue;
      m.set(plan.currency, (m.get(plan.currency) ?? 0) + plan.monthly_amount_minor);
    }
    return m;
  }, [plansAll, subsAll]);

  // Largest revenue first, so a first visit lands on the currency that matters most.
  const moneyCurrencies = useMemo(() => {
    const weight = new Map<string, number>();
    for (const [c, v] of mrrByCurrency) weight.set(c, (weight.get(c) ?? 0) + v);
    for (const [c, v] of pastDueByCurrency) weight.set(c, (weight.get(c) ?? 0) + v);
    return Array.from(weight.entries()).sort(([, a], [, b]) => b - a).map(([c]) => c);
  }, [mrrByCurrency, pastDueByCurrency]);
  const [displayCurrency, setDisplayCurrency] = useDisplayCurrency(moneyCurrencies);
  const otherCurrencies = moneyCurrencies.filter((c) => c !== displayCurrency);
  const othersHint = otherCurrencies.length > 0 ? t('billing.currency.othersHint', { list: otherCurrencies.join(', ') }) : null;
  const mrrValue = amountIn(mrrByCurrency, displayCurrency);
  const arpt = amountIn(arptByCurrency, displayCurrency);
  const pastDueValue = amountIn(pastDueByCurrency, displayCurrency);

  const strandedPlans = useMemo(() => rows.filter(archivedWithSubs).length, [rows]);
  // An archived plan nobody is on grants no credits, so it cannot lose money.
  const losingPlans = useMemo(() => rows.filter((p) => overCeiling(p) && (p.is_active || p.subscribers > 0)).length, [rows]);

  const filterFields: FilterField[] = useMemo(() => {
    const currencies = Array.from(new Set(plansAll.map((p) => p.currency))).sort();
    return [
      {
        key: 'status',
        label: t('billing.plans.filters.status'),
        placeholder: t('billing.plans.filters.anyStatus'),
        options: [
          { value: 'active', label: t('status.active') },
          { value: 'archived', label: t('billing.plans.archived') }
        ]
      },
      {
        key: 'price',
        label: t('billing.plans.filters.price'),
        placeholder: t('billing.plans.filters.anyPrice'),
        options: [
          { value: 'free', label: t('billing.plans.kpi.free') },
          { value: 'paid', label: t('billing.plans.kpi.paid') }
        ]
      },
      { key: 'currency', label: t('common.currency'), placeholder: t('billing.ledger.filters.anyCurrency'), options: currencies.map((c) => ({ value: c, label: c })) }
    ];
  }, [plansAll, t]);

  const hasActiveFilters = Boolean(query.trim()) || Object.values(filters).some((v) => v && v !== 'all');
  function clearAllFilters() { setQuery(''); setFilters({}); setPage(1); }

  const pills: ActiveFilterPill[] = [];
  if (filters.status) pills.push({ id: 'status', field: t('billing.plans.filters.status'), value: filters.status === 'active' ? t('status.active') : t('billing.plans.archived') });
  if (filters.price) pills.push({ id: 'price', field: t('billing.plans.filters.price'), value: filters.price === 'free' ? t('billing.plans.kpi.free') : t('billing.plans.kpi.paid') });
  if (filters.currency) pills.push({ id: 'currency', field: t('common.currency'), value: filters.currency });
  function removePill(id: string) {
    setPage(1);
    setFilters((prev) => { const next = { ...prev }; delete next[id]; return next; });
  }

  const filtered = useMemo(() => {
    let out = rows;
    if (filters.status === 'active') out = out.filter((r) => r.is_active);
    if (filters.status === 'archived') out = out.filter((r) => !r.is_active);
    if (filters.price === 'free') out = out.filter((r) => r.monthly_amount_minor === 0);
    if (filters.price === 'paid') out = out.filter((r) => r.monthly_amount_minor > 0);
    if (filters.currency) out = out.filter((r) => r.currency === filters.currency);
    const q = query.trim().toLowerCase();
    if (q) out = out.filter((r) => r.code.toLowerCase().includes(q) || r.name.toLowerCase().includes(q));
    return out;
  }, [rows, filters, query]);

  useEffect(() => { if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1); }, [filtered.length, page, pageSize]);
  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  function SubscriberBadge({ plan }: { plan: PlanRow }) {
    const badge = (
      <Badge size="xs" color={archivedWithSubs(plan) ? 'red' : 'blue'} variant="light">
        {formatInteger(plan.subscribers)}
      </Badge>
    );
    if (!archivedWithSubs(plan)) return badge;
    return <Tooltip label={t('billing.plans.archivedWithSubs')} position="top">{badge}</Tooltip>;
  }

  /**
   * The credits a plan includes over the most its fee can carry, so the two
   * figures that decide whether a plan can lose money read as one. A stack
   * rather than `TwoLineCell`, which truncates the second line in a narrow
   * right-aligned column.
   */
  function CreditsCell({ plan }: { plan: PlanRow }) {
    const ceiling = formatInteger(wholeCredits(plan.ceiling_minor));
    return (
      <Stack gap="xs" align="flex-end">
        <Text role="bodySmall" fw={600} className="rr-billing-money" c={overCeiling(plan) ? 'var(--rr-color-error)' : undefined}>
          {formatCredits(plan.included_credits_minor)}
        </Text>
        <Tooltip label={t('billing.plans.ceiling.hint')} position="top">
          {overCeiling(plan)
            ? <Badge size="xs" variant="light" color="red">{t('billing.plans.ceiling.over', { ceiling })}</Badge>
            : <Text role="caption" c={MUTED} style={{ whiteSpace: 'nowrap' }}>{t('billing.plans.ceiling.upTo', { ceiling })}</Text>}
        </Tooltip>
      </Stack>
    );
  }

  const loading = plansQ.isLoading || subsQ.isLoading;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('billing.plans.eyebrow')}
        title={t('billing.plans.title')}
        description={t('billing.plans.description')}
        actions={
          <Group gap="sm" wrap="nowrap">
            {/* There is no create-plan endpoint: plans arrive by migration, so
                the header offers the roster rather than a button that cannot
                do what it says. */}
            <Button size="sm" variant="outline" onClick={() => nav('/billing/subscriptions')}>
              {t('billing.plans.viewRoster')}
            </Button>
            <ViewToggle value={view === 'cards' ? 'table' : view} onChange={setView} />
          </Group>
        }
      />

      <CurrencySwitch currencies={moneyCurrencies} value={displayCurrency} onChange={setDisplayCurrency} />

      <SimpleGrid cols={STAT_COLS} spacing="md">
        <StatCard
          tone="success" icon={<IconTrend />}
          label={t('billing.plans.metric.mrr')}
          value={mrrValue || t('billing.subscriptions.metric.noRevenue')}
          hint={othersHint ?? t('billing.plans.metric.mrrHint')}
          loading={loading}
        />
        <StatCard
          tone="primary" icon={<IconLayers />}
          label={t('billing.plans.metric.activeSubs')}
          value={formatInteger(activeSubs)}
          hint={t('billing.plans.metric.activeSubsHint')}
          loading={loading}
          to="/billing/subscriptions"
          linkLabel={t('billing.overview.link.subscriptions')}
        />
        <StatCard
          tone="info" icon={<IconWallet />}
          label={t('billing.plans.metric.arpt')}
          value={arpt || t('billing.subscriptions.metric.noRevenue')}
          hint={othersHint ?? t('billing.plans.metric.arptHint')}
          loading={loading}
        />
        <StatCard
          tone="warning" icon={<IconClock />}
          label={t('billing.plans.metric.pastDue')}
          value={pastDueValue || t('billing.plans.metric.noPastDue')}
          hint={t('billing.plans.metric.pastDueHint')}
          loading={loading}
          to="/billing/subscriptions?status=past_due"
          linkLabel={t('billing.overview.link.subscriptions')}
        />
      </SimpleGrid>

      {losingPlans > 0 && (
        <Alert color="error" title={t('billing.plans.overCeilingBanner.title', { count: losingPlans })}>
          {t('billing.plans.overCeilingBanner.body')}
        </Alert>
      )}

      {strandedPlans > 0 && (
        <Alert color="warning" title={t('billing.plans.strandedBanner.title', { count: strandedPlans })}>
          {t('billing.plans.strandedBanner.body')}
        </Alert>
      )}

      <BillingToolbar
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('billing.plans.search')}
      >
        <FilterDrawer
          fields={filterFields}
          values={filters}
          onApply={(next) => { setFilters(next); setPage(1); }}
          onClear={clearAllFilters}
        />
      </BillingToolbar>

      <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />

      <TableShell
        totalLabel={t('billing.plans.showingCount', { shown: formatInteger(filtered.length), total: formatInteger(rows.length) })}
        loading={plansQ.isLoading}
        isEmpty={filtered.length === 0}
        empty={<EmptyState title={t('billing.plans.empty')} description={hasActiveFilters ? t('common.tryClearingFilters') : t('billing.plans.emptyRows')} action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined} />}
        error={
          (plansQ.error || subsQ.error) ? (
            <Alert color="error">
              <Stack gap="sm">
                <Text role="bodySmall">
                  {plansQ.error ? t('billing.plans.loadError') : t('billing.plans.subsLoadError')}
                </Text>
                <Group>
                  <Button size="xs" variant="outline" onClick={() => { void plansQ.refetch(); void subsQ.refetch(); }}>
                    {t('action.retry')}
                  </Button>
                </Group>
              </Stack>
            </Alert>
          ) : null
        }
        pagination={
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        }
      >
        {view === 'table' && (
          <DataTable<Indexed<PlanRow>>
            records={paged as Indexed<PlanRow>[]}
            loading={plansQ.isLoading}
            idAccessor="id"
            emptyState={t('billing.plans.emptyRows')}
            columns={[
              {
                accessor: 'code',
                title: t('billing.plans.columns.planCode'),
                sortable: true,
                // A plan code is what an operator types into a subscription
                // request, so it is quoted often enough to earn a copy action.
                render: (r) => <CopyableId value={r.code} label={t('billing.plans.copyCode')} chars={24} />
              },
              {
                accessor: 'name',
                title: t('billing.plans.columns.planName'),
                sortable: true,
                render: (r) => <TwoLineCell primary={<>{r.name}{r.badge ? <> <Badge size="xs" variant="light" color="blue">{r.badge}</Badge></> : null}</>} secondary={r.tagline ?? t('billing.plans.includesCount', { count: formatInteger(r.features.length) })} title={r.name} />
              },
              {
                accessor: 'monthly_amount_minor',
                title: t('billing.plans.columns.monthly'),
                textAlign: 'right',
                sortable: true,
                render: (r) => <Money minor={r.monthly_amount_minor} currency={r.currency} />
              },
              {
                accessor: 'markup',
                title: t('billing.plans.columns.markup'),
                textAlign: 'right',
                sortable: true,
                // What a message costs on the plan is its cost plus this. The
                // entry plan is marked because its markup is the list price.
                render: (r) => (
                  <Stack gap="xs" align="flex-end">
                    <Text role="bodySmall" fw={600} className="rr-billing-money">{`${r.markup}%`}</Text>
                    {r.id === entryId && <Text role="caption" c={MUTED} style={{ whiteSpace: 'nowrap' }}>{t('billing.plans.markup.entry')}</Text>}
                  </Stack>
                )
              },
              {
                accessor: 'included_credits_minor',
                title: t('billing.plans.columns.includedCredits'),
                textAlign: 'right',
                sortable: true,
                // A count of credits, as the drawer edits them and the price
                // list words them, with the most the fee can carry beneath.
                render: (r) => <CreditsCell plan={r} />
              },
              {
                accessor: 'subscribers',
                title: t('billing.plans.columns.activeSubs'),
                textAlign: 'right',
                // Sortable because "which plan is actually selling" is the
                // question a catalogue exists to answer.
                sortable: true,
                render: (r) => <SubscriberBadge plan={r} />
              },
              {
                accessor: 'mrr_minor',
                title: t('billing.plans.columns.mrr'),
                textAlign: 'right',
                sortable: true,
                render: (r) => <Money minor={r.mrr_minor} currency={r.currency} />
              },
              {
                accessor: 'is_active',
                title: t('common.status'),
                render: (r) => <StatusBadge status={r.is_active ? 'healthy' : 'inactive'}>{r.is_active ? t('status.active') : t('billing.plans.archived')}</StatusBadge>
              },
              {
                accessor: 'created_at',
                title: t('billing.plans.columns.created'),
                sortable: true,
                render: (r) => <span title={r.created_at}>{formatDate(r.created_at)}</span>
              },
              {
                accessor: 'actions',
                title: t('common.actions'),
                textAlign: 'right',
                render: (r) => <Button size="xs" variant="outline" onClick={() => setEditing(r)}>{t('billing.plans.edit.action')}</Button>
              }
            ]}
          />
        )}
        {view === 'list' && (
          <Stack gap="xs">
            {paged.map((p) => (
              <div key={p.id} className="rr-list-row" style={{ cursor: 'default' }}>
                <Group gap="md" align="center" wrap="nowrap" style={{ width: '100%' }}>
                  <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                    <Group gap="xs" align="baseline" wrap="nowrap">
                      <Text role="subtitle" fw={600} truncate>{p.name}</Text>
                      <Text role="caption" c={MUTED}><code>{p.code}</code></Text>
                    </Group>
                    <Text role="caption" c={MUTED}>
                      {t('billing.plans.list.meta', {
                        monthly: formatMinor(p.monthly_amount_minor, p.currency),
                        credits: formatCredits(p.included_credits_minor)
                      })}
                      {' · '}
                      {t('billing.plans.list.ladder', { markup: p.markup, ceiling: formatInteger(wholeCredits(p.ceiling_minor)) })}
                      {' · '}
                      {t('billing.plans.list.created', { when: formatDate(p.created_at) })}
                    </Text>
                  </Stack>
                  {overCeiling(p) && (
                    <Badge size="xs" variant="light" color="red">
                      {t('billing.plans.ceiling.over', { ceiling: formatInteger(wholeCredits(p.ceiling_minor)) })}
                    </Badge>
                  )}
                  <SubscriberBadge plan={p} />
                  <StatusBadge status={p.is_active ? 'healthy' : 'inactive'}>{p.is_active ? t('status.active') : t('billing.plans.archived')}</StatusBadge>
                  <Button size="xs" variant="outline" onClick={() => setEditing(p)}>{t('billing.plans.edit.action')}</Button>
                </Group>
              </div>
            ))}
          </Stack>
        )}
        {view === 'cards' && (
          <SimpleGrid cols={GRID_COLS} spacing="md">
            {paged.map((p) => (
              <Card key={p.id} withBorder>
                <Stack gap="md">
                  <Group justify="space-between" align="baseline">
                    <Stack gap="xs">
                      <Text role="subtitle" fw={600}>{p.name}</Text>
                      <Text role="caption" c={MUTED}><code>{p.code}</code></Text>
                    </Stack>
                    <StatusBadge status={p.is_active ? 'healthy' : 'inactive'}>{p.is_active ? t('status.active') : t('billing.plans.archived')}</StatusBadge>
                  </Group>
                  <Group justify="space-between" align="baseline" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
                    <Stack gap="xs">
                      <Text role="caption" c={MUTED}>{t('billing.plans.columns.monthly')}</Text>
                      <Money minor={p.monthly_amount_minor} currency={p.currency} />
                    </Stack>
                    <Stack gap="xs" style={{ textAlign: 'right' }}>
                      <Text role="caption" c={MUTED}>{t('billing.plans.credits')}</Text>
                      <Text role="bodySmall" fw={600} className="rr-billing-money" c={overCeiling(p) ? 'var(--rr-color-error)' : undefined}>
                        {formatCredits(p.included_credits_minor)}
                      </Text>
                    </Stack>
                  </Group>
                  <Text role="caption" c={overCeiling(p) ? 'var(--rr-color-error)' : MUTED}>
                    {t('billing.plans.list.ladder', { markup: p.markup, ceiling: formatInteger(wholeCredits(p.ceiling_minor)) })}
                  </Text>
                  <Group gap="sm" align="center" justify="space-between">
                    <Group gap="sm" align="center">
                      <SubscriberBadge plan={p} />
                      <Text role="caption" c={MUTED}>
                        {t('billing.plans.cardMrr', { mrr: formatMinor(p.mrr_minor, p.currency) })}
                      </Text>
                    </Group>
                    <Button size="xs" variant="outline" onClick={() => setEditing(p)}>{t('billing.plans.edit.action')}</Button>
                  </Group>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </TableShell>

      <PlanEditDrawer plan={editing} opened={editing !== null} onClose={() => setEditing(null)} />

      <Alert color="info" title={t('billing.plans.subsFootnote.title')}>
        <Group justify="space-between" align="center" gap="md" wrap="wrap">
          <Text role="bodySmall">{t('billing.plans.subsFootnote.body')}</Text>
          <Button size="xs" variant="outline" onClick={() => nav('/billing/subscriptions')}>
            {t('billing.plans.viewRoster')}
          </Button>
        </Group>
      </Alert>
    </Stack>
  );
}
